import { useEffect, useRef, memo, useCallback } from "react";

const PARTICLE_COUNT = 55;
const COLORS = [
  "rgba(124, 77, 255, 0.7)",
  "rgba(39, 184, 255, 0.6)",
  "rgba(124, 77, 255, 0.5)",
  "rgba(39, 184, 255, 0.45)",
  "rgba(255, 255, 255, 0.3)",
];

const createParticle = (w, h) => ({
  x: Math.random() * w,
  y: Math.random() * h,
  baseX: 0,
  baseY: 0,
  vx: (Math.random() - 0.5) * 0.3,
  vy: (Math.random() - 0.5) * 0.3,
  size: 1.5 + Math.random() * 3.5,
  color: COLORS[Math.floor(Math.random() * COLORS.length)],
  opacity: 0.2 + Math.random() * 0.6,
  blinkSpeed: 0.005 + Math.random() * 0.015,
  blinkPhase: Math.random() * Math.PI * 2,
});

const ParticleSystem = () => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const animFrameRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    mouseRef.current.x = e.clientX;
    mouseRef.current.y = e.clientY;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };

    resize();

    // Initialize particles
    const w = window.innerWidth;
    const h = window.innerHeight;
    particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () =>
      createParticle(w, h)
    );
    particlesRef.current.forEach((p) => {
      p.baseX = p.x;
      p.baseY = p.y;
    });

    let time = 0;

    const animate = () => {
      time += 1;
      const width = window.innerWidth;
      const height = window.innerHeight;
      ctx.clearRect(0, 0, width, height);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      particlesRef.current.forEach((p) => {
        // Movement
        p.x += p.vx;
        p.y += p.vy;

        // Wrap around edges
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;

        // Mouse parallax — subtle shift
        const dx = mx - p.x;
        const dy = my - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const parallaxRange = 250;
        let offsetX = 0;
        let offsetY = 0;
        if (dist < parallaxRange) {
          const force = (1 - dist / parallaxRange) * 15;
          offsetX = -(dx / dist) * force;
          offsetY = -(dy / dist) * force;
        }

        // Blink effect
        const blink = Math.sin(time * p.blinkSpeed + p.blinkPhase);
        const currentOpacity = p.opacity * (0.5 + 0.5 * blink);

        // Draw
        const drawX = p.x + offsetX;
        const drawY = p.y + offsetY;

        ctx.beginPath();
        ctx.arc(drawX, drawY, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color.replace(
          /[\d.]+\)$/,
          `${currentOpacity})`
        );
        ctx.fill();

        // Glow
        ctx.beginPath();
        ctx.arc(drawX, drawY, p.size * 3, 0, Math.PI * 2);
        const grad = ctx.createRadialGradient(
          drawX, drawY, 0,
          drawX, drawY, p.size * 3
        );
        grad.addColorStop(0, p.color.replace(/[\d.]+\)$/, `${currentOpacity * 0.4})`));
        grad.addColorStop(1, "transparent");
        ctx.fillStyle = grad;
        ctx.fill();
      });

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [handleMouseMove]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[1]"
      style={{ willChange: "transform" }}
    />
  );
};

export default memo(ParticleSystem);
